import { defineStore } from 'pinia'
import { ButtonAction } from '@/Support/Forms/Interfaces/Button'
import { PromptStatus } from '@/Support/Global/Interfaces/PromptStatus'

export interface AlertFormInputText {
  placeholder: string
  key: string
  value: string | null
  rows?: number
}

interface Inputs {
  [index: string]: string
}

interface ModalOptions {
  primaryButtonLabel?: string
  cancelButtonLabel?: string
  disabled?: boolean
  primaryButtonAction?: ButtonAction
  cancelButtonAction?: ButtonAction
  show?: boolean
  bgIconClass?: string
  bgCenteredIconClass?: string
  centered?: boolean
  twoButtonsCentered?: boolean
  wide?: boolean
  closeOnPrimary?: boolean
  onSubmit?: (inputs?: Inputs, textAreas?: Inputs) => {}
  icon?: string
  iconClass?: string
  inputTexts?: AlertFormInputText[]
  textAreas?: AlertFormInputText[]
}

interface State extends ModalOptions {
  title: string
  description?: string | null
}

const defaultState: State = {
  title: 'Alert',
  description: null,
  primaryButtonLabel: 'Accept',
  cancelButtonLabel: 'Cancel',
  cancelButtonAction: ButtonAction.Cancel,
  primaryButtonAction: ButtonAction.Primary,
  bgIconClass:
    'mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-secondary-100 dark:bg-secondary-600 sm:mx-0 sm:h-10 sm:w-10',
  bgCenteredIconClass:
    'mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 dark:bg-secondary-600',
  centered: false,
  twoButtonsCentered: false,
  wide: true,
  closeOnPrimary: false,
  disabled: false,
  show: false,
  onSubmit: () => {},
  icon: 'success',
  iconClass: 'h-6 w-6 text-primary-600 dark:text-primary-500',
  inputTexts: [],
  textAreas: [],
}

export const useAlertModal = defineStore('alertModal', {
  state: (): State => {
    return defaultState
  },
  actions: {
    newAlert(options: State) {
      for (const [key, value] of Object.entries(options)) {
        this[key] = value
      }
      this.show = true
    },

    newPromptAlert(title: string, description: string, options?: ModalOptions) {
      this.newAlert({
        title,
        description,
        centered: false,
        twoButtonsCentered: false,
        wide: true,
        ...options,
      })
    },

    newPromptStatusAlert(
      status: PromptStatus,
      title: string,
      description: string,
      primaryButtonLabel: string,
      onSubmit?: () => {},
    ) {
      switch (status) {
        case PromptStatus.Danger:
          this.newPromptDangerAlert(
            title,
            description,
            primaryButtonLabel,
            onSubmit,
          )
          break

        case PromptStatus.Warning:
          this.newPromptWarningAlert(
            title,
            description,
            primaryButtonLabel,
            onSubmit,
          )
          break
      }
    },

    newPromptWarningAlert(
      title: string,
      description: string,
      primaryButtonLabel: string,
      onSubmit?: () => {},
    ) {
      this.newPromptAlert(title, description, {
        bgIconClass:
          'mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-accent-100 dark:bg-accent-950 sm:mx-0 sm:h-10 sm:w-10',
        icon: 'warning',
        iconClass: 'h-6 w-6 text-accent-600 dark:text-accent-500',
        primaryButtonAction: ButtonAction.Warning,
        primaryButtonLabel,
        onSubmit,
      })
    },

    newPromptDangerAlert(
      title: string,
      description: string,
      primaryButtonLabel: string,
      onSubmit?: () => null,
    ) {
      this.newPromptAlert(title, description, {
        bgIconClass:
          'mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-danger-100 dark:bg-danger-950 sm:mx-0 sm:h-10 sm:w-10',
        icon: 'danger',
        iconClass: 'h-6 w-6 text-danger-600 dark:text-danger-500',
        primaryButtonAction: ButtonAction.Danger,
        primaryButtonLabel,
        onSubmit,
      })
    },

    newForm(title: string, description: string, options?: ModalOptions) {
      const config = {
        title,
        description,
        primaryButtonLabel: 'Accept',
        cancelButtonLabel: 'Cancel',
        cancelButtonAction: ButtonAction.Cancel,
        primaryButtonAction: ButtonAction.Primary,
        icon: 'edit',
      }
      this.newAlert({
        ...config,
        ...options,
      })
    },

    newInfoAlert(title: string, description: string, options?: ModalOptions) {
      const config = {
        title,
        description,
        wide: false,
        centered: true,
        twoButtonsCentered: false,
        closeOnPrimary: true,
        primaryButtonLabel: 'Accept',
        cancelButtonLabel: 'Cancel',
        cancelButtonAction: ButtonAction.Cancel,
        primaryButtonAction: ButtonAction.Primary,
      }
      this.newAlert({
        ...config,
        ...options,
      })
    },

    newErrorAlert(title: string, description: string, options?: ModalOptions) {
      this.newInfoAlert(title, description, {
        bgCenteredIconClass:
          'mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger-100 dark:bg-danger-600',
        icon: 'warning',
        iconClass: 'h-6 w-6 text-danger-600 dark:text-danger-500',
        ...options,
      })
    },

    newOptionErrorAlert(
      title: string,
      description: string,
      buttonLabel: string,
      onSubmit: () => null,
    ) {
      this.newErrorAlert(title, description, {
        primaryButtonLabel: buttonLabel,
        twoButtonsCentered: true,
        onSubmit,
      })
    },

    resetState() {
      for (const [key, value] of Object.entries(defaultState)) {
        this[key] = value
      }
    },
  },
})
